import { format } from 'date-fns';
import { NullableDate } from 'mapistry-shared';
import React from 'react';
import { Form, useFormState } from 'react-final-form';
import CalendarIcon from '../../../public/svg/calendar.svg';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useOpenClose } from '../hooks/useOpenClose';
import { Button } from './Button';
import { DateRangePickerField } from './fields/DateRangePickerField';
import { BaseModal } from './modals/BaseModal';

type DateRangeFilterModalProps = Omit<
  React.ComponentProps<typeof BaseModal>,
  'buttons' | 'title'
>;

type FormValues = {
  from: NullableDate;
  to: NullableDate;
};

type DateRangeFilterModalBodyProps = DateRangeFilterModalProps & {
  handleSubmit: () => void;
};

function DateRangeFilterModalBody({
  handleSubmit,
  onClose,
  open,
}: DateRangeFilterModalBodyProps) {
  const { invalid } = useFormState<FormValues>({
    subscription: { invalid: true },
  });

  return (
    <BaseModal
      buttons={
        <>
          <Button color="primary" onClick={onClose} type="button">
            Cancel
          </Button>

          <Button
            color="primary"
            disabled={invalid}
            onClick={handleSubmit}
            type="submit"
            variant="contained"
          >
            Filter
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      showCloseButton
      title="Filter date range"
    >
      <DateRangePickerField />
    </BaseModal>
  );
}

function DateRangeFilterModal({ onClose, open }: DateRangeFilterModalProps) {
  const { filter, setFilter } = useDateRangeFilter();

  const onSubmit = (values: FormValues) => {
    setFilter({ from: values.from ?? null, to: values.to ?? null });
    onClose?.();
  };

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={{ from: filter?.from ?? null, to: filter?.to ?? null }}
      subscription={{}}
    >
      {({ handleSubmit }) => (
        <DateRangeFilterModalBody
          handleSubmit={handleSubmit}
          onClose={onClose}
          open={open}
        />
      )}
    </Form>
  );
}

export function DateRangeFilterButton() {
  const [isOpen, open, close] = useOpenClose();
  const { filter } = useDateRangeFilter();
  const hasFilter = !!filter && !!(filter.from || filter.to);

  let label = 'Filter date range';
  const fromDateLabel = filter?.from ? format(filter.from, 'MM/dd/yyyy') : null;
  const toDateLabel = filter?.to ? format(filter.to, 'MM/dd/yyyy') : null;
  if (fromDateLabel && toDateLabel) {
    label = `${fromDateLabel} - ${toDateLabel}`;
  } else if (fromDateLabel) {
    label = `After ${fromDateLabel}`;
  } else if (toDateLabel) {
    label = `Before ${toDateLabel}`;
  }

  return (
    <>
      <Button
        onClick={open}
        color={hasFilter ? 'primary' : undefined}
        variant={hasFilter ? 'outlined' : undefined}
      >
        <CalendarIcon />
        {label}
      </Button>
      <DateRangeFilterModal onClose={close} open={isOpen} />
    </>
  );
}
